import { CBDocumentStatusValue, CBDossierKindKeys, CBOrigin, CBOriginIdentifier, DOSSIER_ATTACHMENT_CONFIG_FIELD_TO_EXCLUDE, DossierTemplateDto, ICheckDrivingLicenceRequest, IDocParam, IDossierFormValues, IUpdateDocumentStatusRequest } from "../../types";
import Box from "@mui/material/Box";
import CustomerBaseAttachmentDetail from "./CustomerBaseAttachmentDetails"; 
import DocumentModal from "../modal/DocumentModal";
import { useContext, useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { attachmentCreateQueryFieldsState, attachmentUpdateQueryFieldsState, checkDrivingLicenceQueryFieldsState, updateAttachmentStatusQueryFieldsState, updateDossierStatusQueryFieldsState } from "../../state/customerbaseState";
import dayjs from "dayjs";
import { Skeleton } from "@mui/material/index";
import { v4 } from "uuid";
import CustomerBaseDossiersActionsBar from "./CustomerBaseDossiersActionsBar";
import CustomerBaseDossierTemplatePicker from "./CustomerBaseDossierTemplatePicker";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

interface CustomerBaseDossiersPageProps {
    templates: DossierTemplateDto[],
    dossierTemplateIdentifier: string,
    cbId: string,
    cbFullName: string,
    isOrganization: boolean,
    isLoading: boolean,
    //dossier: DossierDto | undefined 
}

const CustomerBaseDossiersPage = (props: CustomerBaseDossiersPageProps) => {

    const { globalData } = useContext(GlobalContext);
    const dossierFormContext = useFormContext<IDossierFormValues>();
    const attachmentFieldArray = useFieldArray<IDossierFormValues, 'attachments'>({
        name: "attachments",
        control: dossierFormContext.control
    });
    const watchAttachmentFieldArray = useWatch<IDossierFormValues, 'attachments'>({
        name: "attachments",
        control: dossierFormContext.control
    });
    const controlledAttachmentFieldArray = attachmentFieldArray.fields.map((field, index) => {
        return {
            ...field,
            ...watchAttachmentFieldArray[index]
        }
    });

    const [openDocumentModal, setOpenDocumentModal] = useState(false);
    const [openWarningRemoveModal, setOpenWarningRemoveModal] = useState({
        value: false,
        index: -1
    });
    const [editingAttach, setEditingAttach] = useState<IDocParam | undefined>(undefined);
    const [selectedTemplateIdentifier, setSelectedTemplateIdentifier] = useState('');
    const setAttachmentCreateQueryFields = useSetRecoilState(attachmentCreateQueryFieldsState);
    const setAttachmentUpdateQueryFields = useSetRecoilState(attachmentUpdateQueryFieldsState);
    const setCheckDrivingLicenceQueryFields = useSetRecoilState(checkDrivingLicenceQueryFieldsState);
    const setUpdateAttachmentStatusQueryFields = useSetRecoilState(updateAttachmentStatusQueryFieldsState);
    const setUpdateDossierStatusQueryFields = useSetRecoilState(updateDossierStatusQueryFieldsState);

    const disabledAddAttachmentButton = controlledAttachmentFieldArray.length >= (props.templates.find(t => t.identifier == props.dossierTemplateIdentifier)?.attachments?.length || 100);

    const handleOnCheckDrivingLicence = (attachment: any) => {     
        const req: ICheckDrivingLicenceRequest = {
            owner: attachment.owner,
            dossier: attachment.dossier,
            attachment: attachment.identifier
        }
        setCheckDrivingLicenceQueryFields(req);
    }

    const handleAttachmentOnUpdateStatus = (attachment: any, status: string) => {
        const req: IUpdateDocumentStatusRequest = {
            identifier: attachment.identifier,
            owner: attachment.owner,
            status: status
        }
        setUpdateAttachmentStatusQueryFields(req);
    }

    const updateDossierStatus = (status: string) => {
        const owner = dossierFormContext.getValues("owner");
        const identifier = dossierFormContext.getValues("identifier");
        if(!!identifier && !!owner && !!status) {
            const req: IUpdateDocumentStatusRequest = {
                identifier: identifier,
                owner: owner,
                status: status
            }
            
            setUpdateDossierStatusQueryFields(req);
        }
    }

    const addDossier = () => {
        setSelectedTemplateIdentifier(props.dossierTemplateIdentifier);
        setOpenDocumentModal(true);
    }

    return (
        <>
        {props.isLoading ?
            <Box p={1} >
                {
                    Array.from(new Array(3)).map(() => {
                        return <Box p={1} key={`box-${v4()}`}><Skeleton key={`skeleton-${v4()}`} variant="rectangular" width="100%" height={250} animation={"wave"}/></Box>

                    })
                }
                
            </Box>
            :
            ((controlledAttachmentFieldArray.length == 0) ?
                <CustomerBaseDossierTemplatePicker
                    templates={props.templates}
                    selectedTemplateIdentifier={selectedTemplateIdentifier}
                    isOrganization={props.isOrganization}
                    onTemplatePicked={(templateIdentifier) => setSelectedTemplateIdentifier(templateIdentifier)}
                    onAddClick={() => setOpenDocumentModal(true)}
                    onBackClick={() => setSelectedTemplateIdentifier('')}            
                />
            :
                <Box p={1}>
                    <CustomerBaseDossiersActionsBar 
                        controlledAttachmentFieldArray={controlledAttachmentFieldArray}
                        disabledAddAttachmentButton={disabledAddAttachmentButton}
                        onAdd={addDossier}
                        updateDossierStatus={updateDossierStatus}
                />
                    <Box m={3}>
                        {
                            controlledAttachmentFieldArray.map((attachment, index) => 
                                <CustomerBaseAttachmentDetail
                                    key={`cb-att-${attachment.id}`}
                                    index={index}
                                    currentKind={attachment.type?.identifier || ''}
                                    numOfTypePages={attachment.numOfTypePages || 2}
                                    fieldsLength={attachment.fields.length}
                                    onSetEditing={() => {
                                        setSelectedTemplateIdentifier(props.dossierTemplateIdentifier);
                                        setEditingAttach({
                                            owner: attachment.owner,
                                            dossier: attachment.dossier,
                                            identifier: attachment.identifier,
                                            number: attachment.number,
                                            kindDescription: attachment.type?.title,
                                            kindIdentifier: attachment.type?.identifier,
                                            kindValue: attachment.type?.kind,
                                            dossierTemplateIdentifier: props.dossierTemplateIdentifier,
                                            status: attachment.status,
                                            issueDate: attachment.type?.identifier == CBDossierKindKeys.SELFIE ? null : dayjs(Number(attachment.issueDate)).format("YYYY-MM-DD"),
                                            expireDate: attachment.type?.identifier == CBDossierKindKeys.SELFIE ? null : dayjs(Number(attachment.expireDate)).format("YYYY-MM-DD"),
                                            country: attachment.country,
                                            fields: attachment.fields.filter(configField => !!configField.type && !DOSSIER_ATTACHMENT_CONFIG_FIELD_TO_EXCLUDE.includes(configField.type?.name || '')).map(field => ({
                                                owner: field.owner,
                                                dossier: field.dossier,
                                                attachment: field.attachment,
                                                type: {
                                                    description: field.type.description,
                                                    name: field.type.name,
                                                    title: field.type.title,
                                                    type: field.type.type,
                                                    values: [...field.type.values],
                                                    tags: [...field.type.tags]
                                                },
                                                value: field.value,
                                                values: [...field.values]
                                            })) || [],
                                            pages: attachment.pages?.map(page => ({
                                                owner: page.owner,
                                                dossier: page.dossier,
                                                attachment: page.attachment,
                                                type: page.type,
                                                status: page.status,
                                                origin: page.origin,
                                                originIdentifier: page.originIdentifier,
                                                creationTime: page.creationTime,
                                                updateTime: page.updateTime,
                                                note: page.note
                                            })) || [],
                                            isNewFrontPage: false,
                                            isNewBackPage: false,
                                            frontPage: !!attachment.frontPage ? new File([attachment.frontPage], "") : null,
                                            backPage: !!attachment.backPage ? new File([attachment.backPage], "") : null
                                        });
                                        setOpenDocumentModal(true);
                                    }}
                                    //onRemove={() => setOpenWarningRemoveModal({value: true, index: index})}
                                    onCheckDrivingLicence={attachment.type?.identifier == CBDossierKindKeys.LICENCE ? () => handleOnCheckDrivingLicence(attachment) : undefined}
                                    onUpdateStatus={(status) => handleAttachmentOnUpdateStatus(attachment, status)}
                                />
                            )
                        }
                    </Box>
                </Box>
            )
        }
        <DocumentModal 
            open={openDocumentModal}
            kindOptions={!!editingAttach ? 
                props.templates.find(t => t.identifier === selectedTemplateIdentifier)?.attachments.map(att => ({key: att.identifier, value: att.kind})) || [] 
                : 
                props.templates.find(t => t.identifier === selectedTemplateIdentifier)?.attachments.map(att => ({key: att.identifier, value: att.kind})).filter(kind => !controlledAttachmentFieldArray.some(f => f.type?.identifier === kind.key)) || []}
            template={props.templates.find(t => t.identifier === selectedTemplateIdentifier)}
            attachment={props.isOrganization}
            updatingDoc={editingAttach}
            onClose={() => {
                setOpenDocumentModal(false);
                if(!!editingAttach) setEditingAttach(undefined);
            }}
            onSave={(docParams) => {
                    console.log("DocParams: ", docParams);
                    //console.log("")
                    
                   /*const q: ICreateAttachmentParams = {
                        request: {
                            attachment: {
                                owner: String(props.cbId) || null,
                                number: docParams.number || null,
                                identifier: docParams.kindIdentifier || null,
                                kind: docParams.kindValue || null,
                                fields: docParams.fields?.filter(f => !!f.value).map(field => ({
                                    owner: String(props.cbId) || null,
                                    value: field.value || null,
                                    type: {
                                        description: field.type?.description || null,
                                        name: field.type?.name || null,
                                        title: field.type?.title || null,
                                        type: field.type?.type || null
                                    }
                                })) || [],
                                title: docParams.kindDescription + " - " + props.cbFullName,
                                issueDate: !!docParams.issueDate ? docParams.issueDate : null,
                                expireDate: !!docParams.expireDate ? docParams.expireDate : null 
                            },
                            attachmentType: docParams.kindIdentifier || null,
                            dossierTemplate: docParams.dossierTemplateIdentifier || null
                        },
                        front: docParams.kindIdentifier !== CBDossierKindKeys.SELFIE ?  (docParams.frontPage || null) : null,
                        back: docParams.backPage || null,
                        selfie: docParams.kindIdentifier == CBDossierKindKeys.SELFIE ? (docParams.frontPage || null) : null
                    };
                    console.log("Create query", q);*/
 
                    //console.log("Stringify: ", JSON.stringify(q.request));
                    
                    if(!!docParams.owner) {
                        const currentAttachment = controlledAttachmentFieldArray.find(att => att.identifier == docParams.identifier) || null;
                        if(!currentAttachment) return;
                        //console.log("Update Current Attachment: ", currentAttachment);
                        /*const qu: IEditAttachmentParams ={
                            request: {
                                owner: docParams.owner,
                                dossier: currentAttachment.dossier || null,
                                type: {
                                    identifier: docParams.kindIdentifier || null,
                                    title: currentAttachment.type?.title || null,
                                    description: currentAttachment.type?.description || null,
                                    kind: docParams.kindValue || null,
                                    fields: currentAttachment.type?.fields.map(field => ({
                                        title: field.title || null,
                                        description: field.description || null,
                                        name: field.name || null,
                                        type: field.type || null,
                                        values: [...field.values],
                                        tags: [...field.tags]
                                    })) || [],
                                    pages: !!currentAttachment.type?.pages ? [...currentAttachment.type?.pages] : [],
                                    tags: !!currentAttachment.type?.tags ? [...currentAttachment.type?.tags] : []
                                },
                                identifier: docParams.identifier || null,
                                number: docParams.number || null,
                                title: currentAttachment.title || null,
                                description: currentAttachment.description || null,
                                status: docParams.status || null,
                                origin: currentAttachment.origin || null,
                                originIdentifier: currentAttachment.originIdentifier || null,
                                issueDate: docParams.issueDate,
                                expireDate: docParams.expireDate,
                                creationTime: currentAttachment.creationTime,
                                updateTime: currentAttachment.updateTime,
                                note: currentAttachment.note || null,
                                fields: docParams.fields?.map(field => ({
                                    owner: field.owner || null,
                                    dossier: field.dossier || null,
                                    attachment: field.attachment,
                                    type: {
                                        title: field.type?.title || null,
                                        description: field.type?.description || null,
                                        name: field.type?.name,
                                        type: field.type?.type || null,
                                        values: !!field.type?.values ? [...field.type?.values] : [],
                                        tags: !!field.type?.tags ? [...field.type.tags] : []
                                    },
                                    value: field.value || null,
                                    values: !!field.values ? [...field.values] : []
                                })),
                                pages: docParams.pages?.map(page => ({
                                    owner: page.owner || null,
                                    dossier: page.dossier || null,
                                    attachment: page.attachment || null,
                                    type: page.type || null,
                                    status: page.status || null,
                                    origin: page.origin || null,
                                    originIdentifier: page.originIdentifier || null,
                                    creationTime: page.creationTime || null,
                                    updateTime: page.updateTime || null,
                                    note: page.note || null
                                })) || [],
                                country: docParams.country || null
                            },
                            front: docParams.isNewFrontPage && docParams.kindIdentifier !== CBDossierKindKeys.SELFIE ?  (docParams.frontPage || null) : null,
                            back: docParams.isNewBackPage ? docParams.backPage || null : null,
                            selfie: docParams.kindIdentifier == CBDossierKindKeys.SELFIE ? (docParams.frontPage || null) : null
                        };

                        console.log("Update Query: ", qu);*/
                        setAttachmentUpdateQueryFields({
                            request: {
                                owner: docParams.owner,
                                dossier: currentAttachment.dossier || null,
                                type: {
                                    identifier: docParams.kindIdentifier || null,
                                    title: currentAttachment.type?.title || null,
                                    description: currentAttachment.type?.description || null,
                                    kind: docParams.kindValue || null,
                                    fields: currentAttachment.type?.fields.map(field => ({
                                        title: field.title || null,
                                        description: field.description || null,
                                        name: field.name || null,
                                        type: field.type || null,
                                        values: [...field.values],
                                        tags: [...field.tags]
                                    })) || [],
                                    pages: !!currentAttachment.type?.pages ? [...currentAttachment.type?.pages] : [],
                                    tags: !!currentAttachment.type?.tags ? [...currentAttachment.type?.tags] : []
                                },
                                identifier: docParams.identifier || null,
                                number: docParams.number || null,
                                title: currentAttachment.title || null,
                                description: currentAttachment.description || null,
                                status: docParams.status || null,
                                origin: CBOrigin.BACKOFFICE,
                                originIdentifier: CBOriginIdentifier.CUSTOMERCARE + (globalData?.user?.username || ''),
                                issueDate: docParams.issueDate,
                                expireDate: docParams.expireDate,
                                creationTime: currentAttachment.creationTime,
                                updateTime: currentAttachment.updateTime,
                                note: currentAttachment.note || null,
                                fields: docParams.fields?.map(field => ({
                                    owner: field.owner || null,
                                    dossier: field.dossier || null,
                                    attachment: field.attachment,
                                    type: {
                                        title: field.type?.title || null,
                                        description: field.type?.description || null,
                                        name: field.type?.name,
                                        type: field.type?.type || null,
                                        values: !!field.type?.values ? [...field.type?.values] : [],
                                        tags: !!field.type?.tags ? [...field.type.tags] : []
                                    },
                                    value: field.value || null,
                                    values: !!field.values ? [...field.values] : []
                                })),
                                pages: docParams.pages?.map(page => ({
                                    owner: page.owner || null,
                                    dossier: page.dossier || null,
                                    attachment: page.attachment || null,
                                    type: page.type || null,
                                    status: page.status || null,
                                    origin: page.origin || null,
                                    originIdentifier: page.originIdentifier || null,
                                    creationTime: page.creationTime || null,
                                    updateTime: page.updateTime || null,
                                    note: page.note || null
                                })) || [],
                                country: docParams.country || null
                            },
                            front: docParams.isNewFrontPage && docParams.kindIdentifier !== CBDossierKindKeys.SELFIE ?  (docParams.frontPage || null) : null,
                            back: docParams.isNewBackPage ? docParams.backPage || null : null,
                            selfie: /*docParams.isNewFrontPage &&*/ docParams.kindIdentifier == CBDossierKindKeys.SELFIE ? (docParams.frontPage || null) : null
                        });
                    }
                    else {
                        setAttachmentCreateQueryFields({
                            request: {
                                attachment: {
                                    owner: String(props.cbId) || null,
                                    number: docParams.number || null,
                                    identifier: docParams.kindIdentifier || null,
                                    kind: docParams.kindValue || null,
                                    fields: docParams.fields?.filter(f => !!f.value).map(field => ({
                                        owner: String(props.cbId) || null,
                                        value: field.value || null,
                                        type: {
                                            description: field.type?.description || null,
                                            name: field.type?.name || null,
                                            title: field.type?.title || null,
                                            type: field.type?.type || null
                                        }
                                    })) || [],
                                    title: docParams.kindDescription + " - " + props.cbFullName,
                                    issueDate: !!docParams.issueDate ? docParams.issueDate : null,
                                    expireDate: !!docParams.expireDate ? docParams.expireDate : null,
                                    origin: CBOrigin.BACKOFFICE,
                                    originIdentifier: CBOriginIdentifier.CUSTOMERCARE + (globalData?.user?.username || '')
                                },
                                attachmentType: docParams.kindIdentifier || null,
                                dossierTemplate: docParams.dossierTemplateIdentifier || null
                            },
                            front: docParams.kindIdentifier !== CBDossierKindKeys.SELFIE ?  (docParams.frontPage || null) : null,
                            back: docParams.backPage || null,
                            selfie: docParams.kindIdentifier == CBDossierKindKeys.SELFIE ? (docParams.frontPage || null) : null
                        });
                    }

                    setOpenDocumentModal(false);
                    if(!!editingAttach) setEditingAttach(undefined);

                }
                    
            }
        />
        {/*<CustomerBaseModal 
            open={openWarningRemoveModal.value}
            contentDescription={t("editingModal.descriptionRemoveWarning")}
            buttonConfirmDescription={t("editingModal.buttons.confirm")} 
            onConfirm={() => {
                if(openWarningRemoveModal.index != -1){
                    
                    //controlledAttachmentFieldArray
                    attachmentFieldArray.remove(openWarningRemoveModal.index);
                    //onSubmit();

                    /
                        CONTROLLA CHE UNA VOLTA RIMOSSO L'ULTIMO TEMPLATE SI MOSTRI LA SCELTA DEI TEMPLATES
                        E NON CHE AVENDO (FORSE) LA selectedTemplateIdentifier POPOLATA MOSTRI DI DEFAULT 
                        L'OPZIONE DI ADD.
                        NON DOVREBBE ACCADERE IN QUANTO UNA VOLTA RIMOSSO SI DOVREBBE RILANCIARE LA getDossier
                    /
                }
            }}
            buttonCloseDescription={t("editingModal.buttons.close")}
            onClose={() => setOpenWarningRemoveModal({value: false, index: -1})}      
        />*/}
        </>
    )


}

export default CustomerBaseDossiersPage;